// src/components/resumo/InlineContentManager.ts
// ✅ ATUALIZADO: Usa /api/upload/cloudinary-image (não conflita com rotas existentes)

import { getCalloutIconSvg } from './calloutIcons';

export class InlineContentManager {
    private editorRef: React.RefObject<HTMLDivElement | null>;
    private cards: any[];
    private draggingBadge: HTMLElement | null = null;
    private ghostElement: HTMLElement | null = null;
    private dragOffset = { x: 0, y: 0 };
    private editModal: HTMLElement | null = null;
    private editorListenersSetup = false;

    private cleanHTML(html: string): string {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        temp.querySelectorAll('button').forEach(btn => btn.remove());
        return temp.innerHTML;
    }

    private cleanTextOnly(html: string): string {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    }

    constructor(editorRef: React.RefObject<HTMLDivElement | null>, cards: any[]) {
        this.editorRef = editorRef;
        this.cards = cards;
    }

    // ========== INSERIR BADGE (INALTERADO) ==========
    insertBadge(cardId: string, cardIndex: number, displayText: string, savedRange?: Range | null, badgeColor?: string) {
        const editor = this.editorRef.current;
        if (!editor) {
            console.error('❌ Editor não disponível');
            return;
        }

        editor.focus();

        setTimeout(() => {
            const platformAccentLight = getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim() || '#2F7130';
            const color = badgeColor || platformAccentLight;
            const bgColor = badgeColor ? `${badgeColor}18` : '#E8F5E9';
            const borderColor = badgeColor || platformAccentLight;
            const badgeHTML = `<span 
    class="flashcard-badge" 
    data-card-id="${cardId}" 
    data-card-index="${cardIndex}"
    data-editable="true"
    data-badge-color="${color}"
    contenteditable="false"
    style="display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; background: ${bgColor}; border: 1px solid ${borderColor}; border-radius: 20px; color: ${color}; font-size: 13px; font-weight: 600; cursor: grab; user-select: none; margin: 0 4px; vertical-align: middle; position: relative;"
>
    ${displayText}
    <button class="badge-eye-btn" style="background: transparent; border: none; padding: 0; margin: 0; cursor: pointer; display: flex; align-items: center;">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
        </svg>
    </button>
</span>&nbsp;`;

            const selection = window.getSelection();
            if (!selection) {
                console.error('❌ Seleção não disponível');
                return;
            }

            let targetRange: Range | null = null;

            if (savedRange && editor.contains(savedRange.commonAncestorContainer)) {
                targetRange = savedRange;
                try {
                    selection.removeAllRanges();
                    selection.addRange(savedRange);
                } catch (e) {
                    console.warn('⚠️ Não foi possível restaurar range salvo');
                    targetRange = null;
                }
            }

            if (!targetRange && selection.rangeCount > 0) {
                const currentRange = selection.getRangeAt(0);
                if (editor.contains(currentRange.commonAncestorContainer)) {
                    targetRange = currentRange;
                }
            }

            if (!targetRange) {
                console.warn('⚠️ Criando range no final do editor');
                targetRange = document.createRange();
                targetRange.selectNodeContents(editor);
                targetRange.collapse(false);
                selection.removeAllRanges();
                selection.addRange(targetRange);
            }

            if (!editor.contains(targetRange.commonAncestorContainer)) {
                console.error('❌ Range fora do editor! Forçando inserção no final.');
                const fallbackRange = document.createRange();
                fallbackRange.selectNodeContents(editor);
                fallbackRange.collapse(false);
                targetRange = fallbackRange;
                selection.removeAllRanges();
                selection.addRange(targetRange);
            }

            try {
                targetRange.deleteContents();

                const temp = document.createElement('div');
                temp.innerHTML = badgeHTML;
                const frag = document.createDocumentFragment();
                let node;
                while ((node = temp.firstChild)) {
                    frag.appendChild(node);
                }
                targetRange.insertNode(frag);

                targetRange.collapse(false);
                selection.removeAllRanges();
                selection.addRange(targetRange);

                console.log('✅ Badge inserido com sucesso no editor');
            } catch (err) {
                console.error('❌ Erro ao inserir badge:', err);
            }

            setTimeout(() => {
                this.initializeAllBadges();
            }, 50);

            const inputEvent = new Event('input', { bubbles: true });
            editor.dispatchEvent(inputEvent);

        }, 10);
    }

    // ========== SETUP ÚNICO DE EVENT LISTENERS DO EDITOR ==========
    private setupEditorEventListeners() {
        if (this.editorListenersSetup) return;
        const editor = this.editorRef.current;
        if (!editor) return;

        // Disparado pelo MediaResizeManager quando cria legenda em imagem antiga sem legenda
        editor.addEventListener('media-caption-created', () => {
            this.initializeAllCaptions();
        });

        this.editorListenersSetup = true;
    }

    // ========== INICIALIZAR BADGES (INALTERADO) ==========
    initializeAllBadges() {
        const editor = this.editorRef.current;
        if (!editor) return;

        this.setupEditorEventListeners();

        const badges = editor.querySelectorAll('.flashcard-badge[data-editable="true"]');
        console.log(`🔄 Inicializando ${badges.length} badges inline...`);

        badges.forEach(badge => {
            const badgeEl = badge as HTMLElement;

            const oldHandler = (badgeEl as any)._dragHandler;
            if (oldHandler) {
                badgeEl.removeEventListener('mousedown', oldHandler);
            }

            const eyeBtn = badgeEl.querySelector('.badge-eye-btn') as HTMLElement;
            if (eyeBtn) {
                // Limpar handler anterior
                const oldEyeHandler = (eyeBtn as any)._eyeClickHandler;
                if (oldEyeHandler) {
                    eyeBtn.removeEventListener('click', oldEyeHandler);
                }

                const eyeClickHandler = (e: MouseEvent) => {
                    e.stopPropagation();
                    e.preventDefault();

                    const cardIndex = parseInt(badgeEl.dataset.cardIndex || '0');
                    const card = this.cards[cardIndex];
                    if (!card) return;

                    // Remover tooltip anterior se existir
                    const existing = document.querySelector('.badge-inline-tooltip') as HTMLElement | null;
                    if (existing) {
                        const existingBadgeId = (existing as any)._badgeId;
                        if (existingBadgeId === badgeEl.dataset.cardId) {
                            const isShowingFront = existing.dataset.showing === 'front';
                            existing.dataset.showing = isShowingFront ? 'back' : 'front';
                            const label = existing.querySelector('.bit-label') as HTMLElement;
                            const text = existing.querySelector('.bit-text') as HTMLElement;
                            if (label) label.textContent = isShowingFront ? 'RESPOSTA' : 'PERGUNTA';
                            if (text) text.innerHTML = isShowingFront
                                ? this.cleanTextOnly(card.back || '')
                                : this.cleanTextOnly(card.front || '');
                            return;
                        } else {
                            existing.remove();
                        }
                    }

                    // Criar tooltip inline
                    const tooltip = document.createElement('div');
                    tooltip.className = 'badge-inline-tooltip';
                    tooltip.dataset.showing = 'front';
                    (tooltip as any)._badgeId = badgeEl.dataset.cardId;

                    tooltip.innerHTML = `
    <div class="bit-label">PERGUNTA</div>
    <div class="bit-text">${this.cleanTextOnly(card.front || '')}</div>
`;

                    // Posicionar abaixo do badge
                    const rect = badgeEl.getBoundingClientRect();
                    tooltip.style.cssText = `
            position: fixed;
            left: ${rect.left}px;
            top: ${rect.bottom + 6}px;
            z-index: 100000;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            width: 280px;
            box-shadow: 0 8px 24px rgba(0,0,0,0.12);
            font-size: 13px;
            line-height: 1.5;
        `;

                    document.body.appendChild(tooltip);

                    // Ajustar se sair da tela
                    const tRect = tooltip.getBoundingClientRect();
                    if (tRect.right > window.innerWidth) {
                        tooltip.style.left = `${window.innerWidth - tRect.width - 12}px`;
                    }

                    // Fechar ao tirar o mouse do badge
                    const removeTooltip = () => {
                        tooltip.remove();
                        badgeEl.removeEventListener('mouseleave', removeTooltip);
                    };
                    badgeEl.addEventListener('mouseleave', removeTooltip);
                };

                (eyeBtn as any)._eyeClickHandler = eyeClickHandler;
                eyeBtn.addEventListener('click', eyeClickHandler);
            }

            const dragHandler = (e: MouseEvent) => {
                if ((e.target as HTMLElement).closest('.badge-eye-btn')) {
                    return;
                }

                this.startBadgeDrag(badgeEl, e);
            };

            (badgeEl as any)._dragHandler = dragHandler;
            badgeEl.addEventListener('mousedown', dragHandler);
        });

        this.initializeAllCaptions();
        this.initializeAllCallouts();

        console.log(`✅ ${badges.length} badges inicializados`);
    }

    initializeAllCaptions() {
        const editor = this.editorRef.current;
        if (!editor) return;

        // Legendas fora do container ocorrem quando o browser normaliza <div> dentro de <span> (HTML inválido).
        // Em vez de remover, tenta recolher ao media-container anterior.
        editor.querySelectorAll('.media-caption-editable').forEach(el => {
            if (!el.closest('.media-container')) {
                let prev = el.previousElementSibling as HTMLElement | null;
                while (prev && !prev.classList.contains('media-container')) {
                    prev = prev.previousElementSibling as HTMLElement | null;
                }
                if (prev?.classList.contains('media-container')) {
                    prev.appendChild(el);
                } else {
                    el.remove();
                }
            }
        });

        const captions = editor.querySelectorAll('.media-caption-editable');
        captions.forEach(caption => {
            const el = caption as HTMLElement;

            if ((el as any)._captionInitialized) return;
            (el as any)._captionInitialized = true;

            // Garante que a legenda preenche a largura do container e acompanha redimensionamentos
            const container = el.closest('.media-container') as HTMLElement | null;
            const mediaEl = container?.querySelector('img, video, iframe') as HTMLElement | null;

            // Estilos garantidos (cobre páginas antigas salvas sem esses valores)
            el.style.width = '100%';
            el.style.maxWidth = '100%';
            el.style.boxSizing = 'border-box';
            el.style.overflowWrap = 'break-word';
            el.style.wordBreak = 'break-word';
            el.style.lineHeight = '1.4';
            el.style.display = 'block';

            if (mediaEl) {
                const syncWidth = () => {
                    const w = mediaEl.offsetWidth;
                    if (w > 0) el.style.maxWidth = w + 'px';
                };

                syncWidth();

                if (mediaEl.tagName === 'IMG' && !(mediaEl as HTMLImageElement).complete) {
                    (mediaEl as HTMLImageElement).addEventListener('load', syncWidth, { once: true });
                }

                if (typeof ResizeObserver !== 'undefined') {
                    const ro = new ResizeObserver(syncWidth);
                    ro.observe(mediaEl);
                }
            }

            el.addEventListener('mouseenter', () => {
                const hint = el.querySelector('.caption-edit-hint') as HTMLElement;
                if (hint) hint.style.opacity = '1';
                el.style.background = 'rgba(0,0,0,0.04)';
            });

            el.addEventListener('mouseleave', () => {
                const hint = el.querySelector('.caption-edit-hint') as HTMLElement;
                if (hint) hint.style.opacity = '0';
                el.style.background = 'transparent';
            });

            el.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                this.openCaptionModal(el, editor);
            });
        });
    }

    private openCaptionModal(el: HTMLElement, editor: HTMLElement) {
        const existingModal = document.querySelector('.caption-modal-overlay');
        if (existingModal) existingModal.remove();

        const isEmpty = el.classList.contains('media-caption-empty');
        const currentText = isEmpty ? '' : (el.dataset.captionText || el.textContent?.replace('✏️', '').trim() || '');

        const overlay = document.createElement('div');
        overlay.className = 'caption-modal-overlay';
        overlay.style.cssText = `
            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5); z-index: 99999;
            display: flex; align-items: center; justify-content: center;
            backdrop-filter: blur(4px);
        `;

        const modal = document.createElement('div');
        modal.style.cssText = `
            background: white; border-radius: 14px; padding: 0;
            width: 420px; max-width: 95vw;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            font-family: 'Inter', -apple-system, sans-serif;
        `;
        modal.innerHTML = `
            <div style="padding: 18px 22px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: space-between;">
                <h3 style="margin: 0; font-size: 16px; font-weight: 700; color: #111827;">Legenda da imagem</h3>
                <button id="cap-modal-close" style="background: transparent; border: none; cursor: pointer; padding: 6px; border-radius: 6px; color: #6b7280; font-size: 18px; line-height: 1;">×</button>
            </div>
            <div style="padding: 20px 22px;">
                <label style="display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px;">Texto da legenda</label>
                <textarea
                    id="cap-modal-input"
                    placeholder="Descreva a imagem..."
                    rows="3"
                    style="
                        width: 100%; padding: 10px 14px; border: 1px solid #d1d5db;
                        border-radius: 8px; font-size: 14px; color: #111827;
                        font-family: inherit; box-sizing: border-box; resize: vertical;
                        outline: none; line-height: 1.5;
                    "
                >${currentText}</textarea>
            </div>
            <div style="padding: 14px 22px; border-top: 1px solid #e5e7eb; display: flex; gap: 10px; justify-content: flex-end;">
                <button id="cap-modal-cancel" style="
                    padding: 9px 18px; background: white; border: 1px solid #d1d5db;
                    border-radius: 8px; font-size: 14px; font-weight: 600; color: #374151;
                    cursor: pointer; font-family: inherit;
                ">Cancelar</button>
                <button id="cap-modal-remove" style="
                    padding: 9px 18px; background: white; border: 1px solid #fca5a5;
                    border-radius: 8px; font-size: 14px; font-weight: 600; color: #dc2626;
                    cursor: pointer; font-family: inherit;
                ">Remover</button>
                <button id="cap-modal-save" style="
                    padding: 9px 18px; background: var(--platform-accent); border: none;
                    border-radius: 8px; font-size: 14px; font-weight: 600; color: white;
                    cursor: pointer; font-family: inherit;
                ">Salvar</button>
            </div>
        `;

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        const textarea = modal.querySelector('#cap-modal-input') as HTMLTextAreaElement;
        setTimeout(() => textarea?.focus(), 50);

        const close = () => overlay.remove();

        modal.querySelector('#cap-modal-close')?.addEventListener('click', close);
        modal.querySelector('#cap-modal-cancel')?.addEventListener('click', close);
        overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

        const applyEmptyStyle = () => {
            el.style.fontSize = '11px';
            el.style.color = '#9ca3af';
            el.style.fontStyle = 'italic';
            el.style.marginTop = '4px';
            el.style.textAlign = 'center';
            el.style.cursor = 'text';
            el.style.padding = '4px 8px';
            el.style.borderRadius = '4px';
            el.style.transition = 'background 0.2s';
            el.style.display = 'block';
            el.style.width = '100%';
            el.style.boxSizing = 'border-box';
            el.style.overflowWrap = 'break-word';
            el.style.wordBreak = 'break-word';
        };

        const applyFilledStyle = () => {
            el.style.fontSize = '12px';
            el.style.color = '#6b7280';
            el.style.fontStyle = 'italic';
            el.style.marginTop = '6px';
            el.style.textAlign = 'center';
            el.style.cursor = 'text';
            el.style.padding = '4px 8px';
            el.style.borderRadius = '4px';
            el.style.transition = 'background 0.2s';
            el.style.display = 'block';
            el.style.width = '100%';
            el.style.boxSizing = 'border-box';
            el.style.overflowWrap = 'break-word';
            el.style.wordBreak = 'break-word';
        };

        modal.querySelector('#cap-modal-remove')?.addEventListener('click', () => {
            el.className = 'media-caption-editable media-caption-empty';
            el.dataset.captionText = '';
            el.innerHTML = `+ Adicionar legenda<span class="caption-edit-hint" style="margin-left: 4px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span>`;
            applyEmptyStyle();
            editor.dispatchEvent(new Event('input', { bubbles: true }));
            close();
        });

        modal.querySelector('#cap-modal-save')?.addEventListener('click', () => {
            const newText = textarea.value.trim();
            if (newText === '') {
                el.className = 'media-caption-editable media-caption-empty';
                el.dataset.captionText = '';
                el.innerHTML = `+ Adicionar legenda<span class="caption-edit-hint" style="margin-left: 4px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span>`;
                applyEmptyStyle();
            } else {
                el.className = 'media-caption-editable';
                el.dataset.captionText = newText;
                el.innerHTML = `${newText}<span class="caption-edit-hint" style="margin-left: 6px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span>`;
                applyFilledStyle();
            }
            editor.dispatchEvent(new Event('input', { bubbles: true }));
            close();
        });
    }

    // ========== BADGE DRAG (INALTERADO) ==========
    private startBadgeDrag(badge: HTMLElement, e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();

        this.draggingBadge = badge;

        const rect = badge.getBoundingClientRect();
        this.dragOffset = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };

        this.ghostElement = badge.cloneNode(true) as HTMLElement;
        this.ghostElement.style.position = 'fixed';
        this.ghostElement.style.left = `${rect.left}px`;
        this.ghostElement.style.top = `${rect.top}px`;
        this.ghostElement.style.width = `${rect.width}px`;
        this.ghostElement.style.opacity = '0.7';
        this.ghostElement.style.pointerEvents = 'none';
        this.ghostElement.style.zIndex = '10000';
        this.ghostElement.style.transform = 'rotate(-3deg) scale(1.05)';
        this.ghostElement.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.5)';
        this.ghostElement.style.transition = 'none';
        document.body.appendChild(this.ghostElement);

        badge.style.opacity = '0.3';
        badge.style.cursor = 'grabbing';

        document.addEventListener('mousemove', this.handleBadgeDragMove);
        document.addEventListener('mouseup', this.handleBadgeDragEnd);

        console.log('🖱️ Iniciando drag inline do badge');
    }

    private handleBadgeDragMove = (e: MouseEvent) => {
        if (!this.draggingBadge || !this.ghostElement) return;
        this.ghostElement.style.left = `${e.clientX - this.dragOffset.x}px`;
        this.ghostElement.style.top = `${e.clientY - this.dragOffset.y}px`;
    };

    private handleBadgeDragEnd = (e: MouseEvent) => {
        if (!this.draggingBadge) return;

        const badge = this.draggingBadge;
        badge.style.opacity = '';
        badge.style.cursor = 'grab';

        const range = document.caretRangeFromPoint?.(e.clientX, e.clientY);

        if (range) {
            const editor = this.editorRef.current;
            if (editor && editor.contains(range.commonAncestorContainer)) {
                try {
                    badge.remove();
                    range.insertNode(badge);

                    const space = document.createTextNode('\u00A0');
                    range.setStartAfter(badge);
                    range.insertNode(space);

                    const selection = window.getSelection();
                    if (selection) {
                        range.setStartAfter(space);
                        range.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }

                    console.log('✅ Badge movido com sucesso');
                } catch (err) {
                    console.error('❌ Erro ao mover badge:', err);
                }
            }
        }

        if (this.ghostElement) {
            this.ghostElement.remove();
            this.ghostElement = null;
        }

        this.draggingBadge = null;

        document.removeEventListener('mousemove', this.handleBadgeDragMove);
        document.removeEventListener('mouseup', this.handleBadgeDragEnd);

        if (this.editorRef.current) {
            const inputEvent = new Event('input', { bubbles: true });
            this.editorRef.current.dispatchEvent(inputEvent);
        }
    };

    // ========== ✅ INSERIR MÍDIA COM CLOUDINARY ==========
    async insertMedia(
        type: 'image' | 'gif' | 'video',
        source: File | string,
        caption?: string,
        savedRange?: Range | null
    ) {
        const editor = this.editorRef.current;
        if (!editor) {
            console.error('❌ Editor não disponível');
            return;
        }

        editor.focus();

        let src: string;
        let isCloudinaryUpload = false;

        // ✅ CASO 1: URL direta
        if (typeof source === 'string') {
            src = source;

            // ✅ YouTube Embed
            if (type === 'video') {
                const videoId = this.extractYouTubeId(src);
                if (videoId) {
                    src = `https://www.youtube.com/embed/${videoId}`;
                    console.log('📺 YouTube detectado:', videoId);
                }
            }
        }
        // ✅ CASO 2: Arquivo - Upload para Cloudinary
        else if (source instanceof File) {
            try {
                console.log('☁️ Fazendo upload para Cloudinary...');

                src = await this.uploadToCloudinary(source);
                isCloudinaryUpload = true;

                console.log('✅ Upload concluído:', src);

            } catch (error) {
                console.error('❌ Erro no upload Cloudinary, usando base64:', error);

                // ✅ FALLBACK: Se Cloudinary falhar
                if (type === 'image' || type === 'gif') {
                    src = await this.compressImage(source);
                } else {
                    src = URL.createObjectURL(source);
                }
            }
        } else {
            console.error('❌ Tipo de source inválido');
            return;
        }

        // ✅ Criar HTML da mídia
        const mediaId = `media-${Date.now()}`;
        let mediaHTML = '';

        if (type === 'image' || type === 'gif') {
            mediaHTML = `<span
                class="media-container"
                data-media-id="${mediaId}"
                data-media-type="${type}"
                contenteditable="false"
                style="display: block; position: relative; margin: 12px 0; cursor: default;"
            ><img
                    src="${src}"
                    alt="${caption || type}"
                    loading="lazy"
                    style="width: 400px; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); display: block;"
                    draggable="false"
                />${caption ? `<span
    class="media-caption-editable"
    data-caption="true"
    data-caption-text="${caption.replace(/"/g, '&quot;')}"
    contenteditable="false"
    style="display: block; font-size: 12px; color: #6b7280; font-style: italic; margin-top: 6px; text-align: center; cursor: text; padding: 4px 8px; border-radius: 4px; transition: background 0.2s; width: 100%; max-width: 100%; box-sizing: border-box; overflow-wrap: break-word; word-break: break-word; line-height: 1.4;"
    title="Clique para editar legenda"
>${caption}<span class="caption-edit-hint" style="margin-left: 6px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span></span>` : `<span
    class="media-caption-editable media-caption-empty"
    data-caption="true"
    data-caption-text=""
    contenteditable="false"
    style="display: block; font-size: 11px; color: #9ca3af; font-style: italic; margin-top: 4px; text-align: center; cursor: text; padding: 4px 8px; border-radius: 4px; transition: background 0.2s; width: 100%; max-width: 100%; box-sizing: border-box; overflow-wrap: break-word; word-break: break-word; line-height: 1.4;"
    title="Clique para adicionar legenda"
>+ Adicionar legenda<span class="caption-edit-hint" style="margin-left: 4px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span></span>`}</span>&nbsp;`;
        } else {
            const isEmbed = src.includes('youtube.com/embed') || src.includes('vimeo.com');
            if (isEmbed) {
                mediaHTML = `<span
                    class="media-container"
                    data-media-id="${mediaId}"
                    data-media-type="video"
                    contenteditable="false"
                    style="display: block; position: relative; margin: 12px 0; cursor: default;"
                ><iframe
                        src="${src}"
                        style="width: 560px; height: 315px; border-radius: 8px; border: none; box-shadow: 0 2px 8px rgba(0,0,0,0.1); display: block;"
                        allowfullscreen
                    ></iframe>${caption ? `<span
    class="media-caption-editable"
    data-caption="true"
    data-caption-text="${caption.replace(/"/g, '&quot;')}"
    contenteditable="false"
    style="display: block; font-size: 12px; color: #6b7280; font-style: italic; margin-top: 6px; text-align: center; cursor: text; padding: 4px 8px; border-radius: 4px; transition: background 0.2s; width: 100%; max-width: 100%; box-sizing: border-box; overflow-wrap: break-word; word-break: break-word; line-height: 1.4;"
    title="Clique para editar legenda"
>${caption}<span class="caption-edit-hint" style="margin-left: 6px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span></span>` : `<span
    class="media-caption-editable media-caption-empty"
    data-caption="true"
    data-caption-text=""
    contenteditable="false"
    style="display: block; font-size: 11px; color: #9ca3af; font-style: italic; margin-top: 4px; text-align: center; cursor: text; padding: 4px 8px; border-radius: 4px; transition: background 0.2s; width: 100%; max-width: 100%; box-sizing: border-box; overflow-wrap: break-word; word-break: break-word; line-height: 1.4;"
    title="Clique para adicionar legenda"
>+ Adicionar legenda<span class="caption-edit-hint" style="margin-left: 4px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span></span>`}</span>&nbsp;`;
            } else {
                mediaHTML = `<span
                    class="media-container"
                    data-media-id="${mediaId}"
                    data-media-type="video"
                    contenteditable="false"
                    style="display: block; position: relative; margin: 12px 0; cursor: default;"
                ><video
                        src="${src}"
                        controls
                        style="width: 400px; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); display: block;"
                    ></video>${caption ? `<span
    class="media-caption-editable"
    data-caption="true"
    data-caption-text="${caption.replace(/"/g, '&quot;')}"
    contenteditable="false"
    style="display: block; font-size: 12px; color: #6b7280; font-style: italic; margin-top: 6px; text-align: center; cursor: text; padding: 4px 8px; border-radius: 4px; transition: background 0.2s; width: 100%; max-width: 100%; box-sizing: border-box; overflow-wrap: break-word; word-break: break-word; line-height: 1.4;"
    title="Clique para editar legenda"
>${caption}<span class="caption-edit-hint" style="margin-left: 6px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span></span>` : `<span
    class="media-caption-editable media-caption-empty"
    data-caption="true"
    data-caption-text=""
    contenteditable="false"
    style="display: block; font-size: 11px; color: #9ca3af; font-style: italic; margin-top: 4px; text-align: center; cursor: text; padding: 4px 8px; border-radius: 4px; transition: background 0.2s; width: 100%; max-width: 100%; box-sizing: border-box; overflow-wrap: break-word; word-break: break-word; line-height: 1.4;"
    title="Clique para adicionar legenda"
>+ Adicionar legenda<span class="caption-edit-hint" style="margin-left: 4px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span></span>`}</span>&nbsp;`;
            }
        }

        // ✅ Inserir no editor
        const selection = window.getSelection();
        if (!selection) return;

        if (savedRange) {
            try {
                selection.removeAllRanges();
                selection.addRange(savedRange);
            } catch (e) {
                console.warn('⚠️ Não foi possível restaurar range');
            }
        }

        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();

            const temp = document.createElement('div');
            temp.innerHTML = mediaHTML;
            const frag = document.createDocumentFragment();
            let node;
            while ((node = temp.firstChild)) {
                frag.appendChild(node);
            }
            range.insertNode(frag);

            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        } else {
            editor.innerHTML += mediaHTML;
        }

        const inputEvent = new Event('input', { bubbles: true });
        editor.dispatchEvent(inputEvent);

        // Registrar listeners de legenda na mídia recém inserida
        setTimeout(() => this.initializeAllCaptions(), 50);

        console.log('✅ Mídia inserida:', { type, mediaId, cloudinary: isCloudinaryUpload });
    }

    // ========== ✅ UPLOAD PARA CLOUDINARY ==========
    private async uploadToCloudinary(file: File): Promise<string> {
        const formData = new FormData();
        formData.append('file', file);

        const token = localStorage.getItem('accessToken');

        // ✅ NOVA URL: /api/upload/cloudinary-image (não conflita!)
        const response = await fetch('/api/upload/cloudinary-image', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao fazer upload');
        }

        const data = await response.json();
        return data.url;
    }

    // ========== EXTRAIR ID DO YOUTUBE ==========
    private extractYouTubeId(url: string): string | null {
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                return match[1];
            }
        }

        return null;
    }

    // ========== COMPRESS IMAGE (FALLBACK) ==========
    private async compressImage(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                const img = new Image();

                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    if (!ctx) {
                        reject(new Error('Erro ao criar canvas'));
                        return;
                    }

                    let width = img.width;
                    let height = img.height;
                    const maxDimension = 1200;

                    if (width > maxDimension || height > maxDimension) {
                        if (width > height) {
                            height = (height / width) * maxDimension;
                            width = maxDimension;
                        } else {
                            width = (width / height) * maxDimension;
                            height = maxDimension;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    ctx.drawImage(img, 0, 0, width, height);

                    const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    resolve(compressedDataUrl);
                };

                img.onerror = () => reject(new Error('Erro ao carregar imagem'));
                img.src = e.target?.result as string;
            };

            reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
            reader.readAsDataURL(file);
        });
    }

    initializeAllCallouts() {
        const editor = this.editorRef.current;
        if (!editor) return;

        const callouts = editor.querySelectorAll('.resumo-callout[data-callout="true"]');
        callouts.forEach(callout => {
            const el = callout as HTMLElement;
            if ((el as any)._calloutInitialized) return;
            (el as any)._calloutInitialized = true;

            // Remover botões e divs salvos de sessões anteriores (evita duplicatas)
            Array.from(el.children).forEach(child => {
                const tag = child.tagName.toLowerCase();
                const isContent = child.classList.contains('callout-title') || child.classList.contains('callout-body');
                if (!isContent && (tag === 'button' || tag === 'div')) child.remove();
            });

            const titleEl = el.querySelector('.callout-title') as HTMLElement;
            const bodyEl = el.querySelector('.callout-body') as HTMLElement;
            const color = el.dataset.calloutColor || getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim() || '#2F7130';

            if (titleEl) {
                titleEl.style.cursor = 'text';
                titleEl.contentEditable = 'true';
                titleEl.addEventListener('dblclick', (e) => e.stopPropagation());
                titleEl.addEventListener('input', () => {
                    editor.dispatchEvent(new Event('input', { bubbles: true }));
                });
            }
            if (bodyEl) {
                bodyEl.style.cursor = 'text';
                bodyEl.contentEditable = 'true';
                bodyEl.addEventListener('dblclick', (e) => e.stopPropagation());
            }

            const isGabarito = el.dataset.gabarito === 'true';
            let gabaritoRevealed = false;

            // Wrapper para os botões de controle (Ocultar + Editar)
            const btnWrapper = document.createElement('div');
            btnWrapper.style.cssText = `
                position: absolute; top: 8px; right: 8px;
                display: flex; gap: 6px; align-items: center;
                opacity: 0; transition: opacity 0.2s; z-index: 10;
            `;
            el.appendChild(btnWrapper);

            // Botão "Ocultar" (só visível quando gabarito está revelado)
            const hideBtn = document.createElement('button');
            hideBtn.textContent = 'Ocultar';
            hideBtn.style.cssText = `
                background: rgba(0,0,0,0.35); color: white; border: none;
                border-radius: 6px; padding: 4px 8px; font-size: 11px;
                font-weight: 600; cursor: pointer; font-family: inherit; display: none;
            `;
            btnWrapper.appendChild(hideBtn);

            const editBtn = document.createElement('button');
            editBtn.textContent = 'Editar caixa';
            editBtn.style.cssText = `
                background: ${color}; color: white; border: none;
                border-radius: 6px; padding: 4px 10px; font-size: 11px;
                font-weight: 600; cursor: pointer; font-family: inherit;
            `;
            btnWrapper.appendChild(editBtn);

            el.addEventListener('mouseenter', () => { btnWrapper.style.opacity = '1'; });
            el.addEventListener('mouseleave', () => { btnWrapper.style.opacity = '0'; });

            // Botão "Mostrar resposta" — centralizado sobre o conteúdo
            const revealBtn = document.createElement('button');
            revealBtn.innerHTML = '&#128065;&nbsp; Mostrar resposta';
            revealBtn.style.cssText = `
                position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
                background: ${color}; color: white; border: none;
                border-radius: 8px; padding: 8px 18px; font-size: 13px;
                font-weight: 600; cursor: pointer; z-index: 20; font-family: inherit;
                display: flex; align-items: center; gap: 6px; white-space: nowrap;
                box-shadow: 0 4px 16px rgba(0,0,0,0.25);
            `;

            const applyGabaritoState = () => {
                const gabMode = el.dataset.gabarito === 'true';
                const shouldBlur = gabMode && !gabaritoRevealed;
                const contentEls = el.querySelectorAll<HTMLElement>('.callout-title, .callout-body');
                contentEls.forEach(ce => {
                    ce.style.filter = shouldBlur ? 'blur(7px)' : '';
                    ce.style.userSelect = shouldBlur ? 'none' : '';
                    ce.style.pointerEvents = shouldBlur ? 'none' : '';
                });
                // Inserir/remover do DOM garante visibilidade sem conflito de CSS
                if (gabMode && !gabaritoRevealed) {
                    if (!revealBtn.parentNode) el.appendChild(revealBtn);
                } else {
                    revealBtn.remove();
                }
                hideBtn.style.display = (gabMode && gabaritoRevealed) ? 'inline-flex' : 'none';
            };

            if (isGabarito) applyGabaritoState();

            revealBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                gabaritoRevealed = true;
                applyGabaritoState();
            });

            hideBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                gabaritoRevealed = false;
                applyGabaritoState();
            });

            const openEditModal = () => {
                if (this.editModal) this.editModal.remove();

                let currentTitleHtml = '';
                if (titleEl) {
                    const titleClone = titleEl.cloneNode(true) as HTMLElement;
                    const iconSpanInClone = titleClone.querySelector('[data-callout-icon-name]');
                    if (iconSpanInClone) iconSpanInClone.remove();
                    currentTitleHtml = titleClone.innerHTML.trim();
                }
                const currentBodyHtml = bodyEl ? bodyEl.innerHTML : '';
                const currentIcon = el.dataset.calloutIcon || '';

                // Detectar cor e borda atual
                let selectedColor = el.dataset.calloutColor || getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim() || '#2F7130';
                let selectedBgColor = el.dataset.calloutBg || '#e8f5e9';
                const hasBorderLeft = !!(el.style.borderLeft || el.style.borderLeftWidth);
                const hasBorderFull = !!(el.style.border && !hasBorderLeft);
                const hasBorderTop = !!(el.style.borderTop && !hasBorderLeft && !hasBorderFull);
                let selectedBorderStyle: 'left' | 'full' | 'top' = hasBorderFull ? 'full' : hasBorderTop ? 'top' : 'left';

                let selectedIcon = currentIcon;
                let isGabaritoEdit = el.dataset.gabarito === 'true';

                // Presets de cor (mesmos do EditorToolbar)
                const colorPresets = [
                    { label: 'Verde GRAIA', border: '#0D3B0E', bg: '#e0ede0' },
                    { label: 'Azul prussiano', border: '#2a4a5a', bg: '#dce8f0' },
                    { label: 'Verde (padrão)', border: '#2F7130', bg: '#e8f5e9' },
                    { label: 'Azul', border: '#2563eb', bg: '#dbeafe' },
                    { label: 'Roxo', border: '#7c3aed', bg: '#ede9fe' },
                    { label: 'Vermelho', border: '#dc2626', bg: '#fee2e2' },
                    { label: 'Amarelo', border: '#ca8a04', bg: '#fef9c3' },
                    { label: 'Cinza', border: '#4b5563', bg: '#f3f4f6' },
                    { label: 'Rosa', border: '#db2777', bg: '#fce7f3' },
                    { label: 'Laranja', border: '#ea580c', bg: '#ffedd5' },
                ];

                const overlay = document.createElement('div');
                overlay.style.cssText = `
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5); z-index: 99999;
                    display: flex; align-items: center; justify-content: center;
                    backdrop-filter: blur(4px);
                `;

                const modal = document.createElement('div');
                modal.style.cssText = `
                    background: white; border-radius: 14px; padding: 0;
                    width: min(960px, 92vw); max-width: 92vw; max-height: 90vh;
                    overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,0.3);
                    font-family: 'Inter', -apple-system, sans-serif;
                    display: flex; flex-direction: column;
                `;

                const icons = [
                    { label: 'Nenhum', value: '' },
                    'flag', 'check-circle', 'alert-triangle', 'info', 'star', 'lightbulb',
                    'target', 'zap', 'pin', 'book-open', 'trophy', 'settings', 'search',
                    'lock', 'heart', 'arrow-right', 'paperclip', 'bookmark',
                    'microscope', 'thermometer', 'activity', 'shield', 'alert-circle',
                ].map(v => typeof v === 'string' ? { label: v, value: v } : v);

                const iconButtonsHtml = icons.map(icon => {
                    const isSelected = icon.value === selectedIcon;
                    const svgContent = icon.value
                        ? getCalloutIconSvg(icon.value, isSelected ? color : '#6b7280')
                        : '∅';
                    return `<button
                        data-icon-val="${icon.value}"
                        style="
                            width: 36px; height: 36px; border-radius: 6px;
                            border: 2px solid ${isSelected ? color : '#e5e7eb'};
                            background: ${isSelected ? `${color}18` : 'white'};
                            cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
                            color: ${isSelected ? color : '#6b7280'};
                            font-size: ${icon.value ? '14px' : '11px'};
                            font-weight: ${icon.value ? '400' : '600'}; transition: all 0.15s;
                        "
                        title="${icon.label}"
                    >${svgContent}</button>`;
                }).join('');

                const colorPresetsHtml = colorPresets.map(p => `
                    <button data-preset-border="${p.border}" data-preset-bg="${p.bg}" title="${p.label}" style="
                        width: 28px; height: 28px; border-radius: 6px;
                        background: ${p.bg}; border: 3px solid ${p.border};
                        cursor: pointer; transition: transform 0.1s;
                        outline: ${selectedColor === p.border ? '2px solid #111' : 'none'};
                        transform: ${selectedColor === p.border ? 'scale(1.18)' : 'scale(1)'};
                    "></button>`).join('');

                modal.innerHTML = `
                    <style>
                        #callout-edit-body ul, #callout-edit-body ol {
                            padding-left: 1.5em; margin: 6px 0; list-style-position: outside;
                        }
                        #callout-edit-body li { margin: 3px 0; }
                        #callout-edit-title:empty::before {
                            content: attr(data-placeholder);
                            color: #9ca3af;
                            pointer-events: none;
                        }
                    </style>
                    <div style="padding: 18px 22px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;">
                        <h3 style="margin: 0; font-size: 17px; font-weight: 700; color: #111827;">Editar caixa de destaque</h3>
                        <button id="callout-modal-close" style="background: transparent; border: none; cursor: pointer; padding: 6px; border-radius: 6px; color: #6b7280; font-size: 20px; line-height: 1;">×</button>
                    </div>
                    <div style="display: flex; flex: 1; overflow: hidden; min-height: 0;">

                        <!-- ESQUERDA: formulário -->
                        <div style="flex: 1; overflow-y: auto; padding: 22px; border-right: 1px solid #e5e7eb; min-width: 0;">

                            <label style="display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px;">Estilo de cor</label>
                            <div id="callout-color-presets" style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 18px;">
                                ${colorPresetsHtml}
                            </div>

                            <label style="display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px;">Estilo de borda</label>
                            <div id="callout-border-btns" style="display: flex; gap: 8px; margin-bottom: 20px;">
                                ${(['left', 'full', 'top'] as const).map(s => `
                                <button data-border-style="${s}" style="
                                    padding: 6px 14px; border-radius: 6px; font-size: 12px; font-weight: 600;
                                    cursor: pointer; font-family: inherit; transition: all 0.15s;
                                    border: 2px solid ${selectedBorderStyle === s ? selectedColor : '#e5e7eb'};
                                    background: ${selectedBorderStyle === s ? `${selectedColor}18` : 'white'};
                                    color: ${selectedBorderStyle === s ? selectedColor : '#374151'};
                                ">${s === 'left' ? '&#x258C; Esquerda' : s === 'full' ? '&#x25A1; Completa' : '&#x2580; Topo'}</button>`).join('')}
                            </div>

                            <label style="display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px;">Ícone</label>
                            <div id="callout-icon-grid" style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 20px;">
                                ${iconButtonsHtml}
                            </div>

                            <label style="display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px;">Título</label>
                            <div style="border: 1px solid #d1d5db; border-radius: 8px; overflow: hidden; margin-bottom: 18px;">
                                <div id="callout-title-toolbar" style="display: flex; gap: 2px; padding: 6px 8px; background: #f9fafb; border-bottom: 1px solid #e5e7eb;">
                                    <button data-title-cmd="bold" title="Negrito" style="width: 30px; height: 30px; border: 1px solid transparent; border-radius: 5px; background: transparent; cursor: pointer; font-weight: 700; font-size: 14px; color: #374151; display: inline-flex; align-items: center; justify-content: center; font-family: inherit;">N</button>
                                    <button data-title-cmd="italic" title="Itálico" style="width: 30px; height: 30px; border: 1px solid transparent; border-radius: 5px; background: transparent; cursor: pointer; font-size: 14px; font-style: italic; color: #374151; display: inline-flex; align-items: center; justify-content: center; font-family: inherit;">I</button>
                                    <button data-title-cmd="underline" title="Sublinhado" style="width: 30px; height: 30px; border: 1px solid transparent; border-radius: 5px; background: transparent; cursor: pointer; font-size: 14px; text-decoration: underline; color: #374151; display: inline-flex; align-items: center; justify-content: center; font-family: inherit;">S</button>
                                </div>
                                <div
                                    id="callout-edit-title"
                                    contenteditable="true"
                                    data-placeholder="Título da caixa (opcional)"
                                    style="min-height: 36px; padding: 8px 14px; font-size: 14px; font-weight: 600; color: #111827; font-family: inherit; outline: none; line-height: 1.6;"
                                ></div>
                            </div>

                            <label style="display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px;">Conteúdo</label>
                            <div style="border: 1px solid #d1d5db; border-radius: 8px; overflow: hidden; margin-bottom: 4px;">
                                <div id="callout-body-toolbar" style="
                                    display: flex; gap: 2px; padding: 6px 8px;
                                    background: #f9fafb; border-bottom: 1px solid #e5e7eb;
                                ">
                                    <button data-cmd="bold" title="Negrito" style="
                                        width: 30px; height: 30px; border: 1px solid transparent; border-radius: 5px;
                                        background: transparent; cursor: pointer; font-weight: 700; font-size: 14px;
                                        color: #374151; display: inline-flex; align-items: center; justify-content: center;
                                        font-family: inherit;
                                    ">N</button>
                                    <button data-cmd="italic" title="Itálico" style="
                                        width: 30px; height: 30px; border: 1px solid transparent; border-radius: 5px;
                                        background: transparent; cursor: pointer; font-size: 14px; font-style: italic;
                                        color: #374151; display: inline-flex; align-items: center; justify-content: center;
                                        font-family: inherit;
                                    ">I</button>
                                    <button data-cmd="underline" title="Sublinhado" style="
                                        width: 30px; height: 30px; border: 1px solid transparent; border-radius: 5px;
                                        background: transparent; cursor: pointer; font-size: 14px; text-decoration: underline;
                                        color: #374151; display: inline-flex; align-items: center; justify-content: center;
                                        font-family: inherit;
                                    ">S</button>
                                    <div style="width: 1px; background: #d1d5db; margin: 4px 2px;"></div>
                                    <button data-cmd="insertUnorderedList" title="Lista com marcadores" style="
                                        width: 30px; height: 30px; border: 1px solid transparent; border-radius: 5px;
                                        background: transparent; cursor: pointer; font-size: 16px;
                                        color: #374151; display: inline-flex; align-items: center; justify-content: center;
                                    ">•</button>
                                    <button data-cmd="insertOrderedList" title="Lista numerada" style="
                                        width: 30px; height: 30px; border: 1px solid transparent; border-radius: 5px;
                                        background: transparent; cursor: pointer; font-size: 11px; font-weight: 700;
                                        color: #374151; display: inline-flex; align-items: center; justify-content: center;
                                        font-family: inherit;
                                    ">1.</button>
                                </div>
                                <div
                                    id="callout-edit-body"
                                    contenteditable="true"
                                    style="
                                        min-height: 100px; max-height: 220px; overflow-y: auto;
                                        padding: 12px 14px; font-size: 14px; color: #111827;
                                        font-family: inherit; outline: none; line-height: 1.6;
                                    "
                                ></div>
                            </div>

                            <!-- MODO GABARITO -->
                            <div style="margin-top: 20px; padding-top: 16px; border-top: 1px solid #e5e7eb;">
                                <label style="display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px;">Modo Gabarito</label>
                                <button id="callout-gabarito-toggle" style="
                                    display: flex; align-items: center; justify-content: center; gap: 8px;
                                    width: 100%; padding: 9px 14px; border-radius: 8px;
                                    border: 2px solid ${isGabaritoEdit ? selectedColor : '#e5e7eb'};
                                    background: ${isGabaritoEdit ? `${selectedColor}18` : 'white'};
                                    color: ${isGabaritoEdit ? selectedColor : '#6b7280'};
                                    font-size: 13px; font-weight: 600; cursor: pointer;
                                    font-family: inherit; transition: all 0.15s; box-sizing: border-box;
                                ">👁 ${isGabaritoEdit ? 'Gabarito ativado' : 'Transformar em gabarito'}</button>
                                <p id="callout-gabarito-hint" style="font-size: 11px; color: #9ca3af; margin: 6px 0 0; line-height: 1.5; display: ${isGabaritoEdit ? 'block' : 'none'};">
                                    O conteúdo ficará embaçado. O leitor clica em "Mostrar resposta" para revelar.
                                </p>
                            </div>
                        </div>

                        <!-- DIREITA: prévia -->
                        <div style="flex: 1; overflow-y: auto; padding: 22px; background: #f9fafb; min-width: 0;">
                            <div style="font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 12px;">PRÉVIA</div>
                            <div id="callout-preview" style="border-radius: 8px; padding: 12px 16px; transition: background 0.2s, border 0.2s;">
                                <p id="callout-preview-title" style="margin: 0 0 6px; font-weight: 700; font-size: 14px; display: none; align-items: center; gap: 6px;"></p>
                                <div id="callout-preview-body" style="font-size: 13px; line-height: 1.6; margin: 0;"></div>
                            </div>
                        </div>
                    </div>
                    <div style="padding: 14px 22px; border-top: 1px solid #e5e7eb; display: flex; gap: 10px; justify-content: space-between; flex-shrink: 0; align-items: center;">
                        <button id="callout-modal-remove" style="
                            padding: 10px 20px; background: white; border: 1px solid #fca5a5;
                            border-radius: 8px; font-size: 14px; font-weight: 600; color: #dc2626;
                            cursor: pointer; font-family: inherit;
                        ">Remover caixa</button>
                        <div style="display: flex; gap: 10px;">
                            <button id="callout-modal-cancel" style="
                                padding: 10px 20px; background: white; border: 1px solid #d1d5db;
                                border-radius: 8px; font-size: 14px; font-weight: 600; color: #374151;
                                cursor: pointer; font-family: inherit;
                            ">Cancelar</button>
                            <button id="callout-modal-save" style="
                                padding: 10px 20px; background: ${selectedColor}; border: none;
                                border-radius: 8px; font-size: 14px; font-weight: 600; color: white;
                                cursor: pointer; font-family: inherit;
                            ">Salvar</button>
                        </div>
                    </div>
                `;

                overlay.appendChild(modal);
                document.body.appendChild(overlay);
                this.editModal = overlay;

                // Populate body with existing HTML
                const bodyEditor = modal.querySelector('#callout-edit-body') as HTMLElement;
                if (bodyEditor) {
                    bodyEditor.innerHTML = currentBodyHtml || '';
                }

                // Populate title with existing HTML
                const titleEditorEl = modal.querySelector('#callout-edit-title') as HTMLElement;
                if (titleEditorEl) {
                    titleEditorEl.innerHTML = currentTitleHtml;
                }

                // Live preview updater
                const updatePreview = () => {
                    const previewContainer = modal.querySelector('#callout-preview') as HTMLElement;
                    const previewTitle = modal.querySelector('#callout-preview-title') as HTMLElement;
                    const previewBody = modal.querySelector('#callout-preview-body') as HTMLElement;
                    if (!previewContainer) return;

                    previewContainer.style.borderLeft = '';
                    previewContainer.style.border = '';
                    previewContainer.style.borderTop = '';
                    if (selectedBorderStyle === 'left') previewContainer.style.borderLeft = `4px solid ${selectedColor}`;
                    else if (selectedBorderStyle === 'full') previewContainer.style.border = `2px solid ${selectedColor}`;
                    else previewContainer.style.borderTop = `4px solid ${selectedColor}`;
                    previewContainer.style.background = selectedBgColor;
                    previewContainer.style.borderRadius = '8px';
                    previewContainer.style.padding = '12px 16px';
                    previewContainer.style.transition = 'background 0.2s, border 0.2s';

                    const titleEditorPreview = modal.querySelector('#callout-edit-title') as HTMLElement;
                    const titleHtmlPreview = titleEditorPreview?.innerHTML || '';
                    const titleIsEmptyPreview = !(titleEditorPreview?.textContent?.trim());
                    if (previewTitle) {
                        if (!titleIsEmptyPreview || selectedIcon) {
                            const iconSvg = selectedIcon ? getCalloutIconSvg(selectedIcon, selectedColor) : '';
                            const iconSpan = iconSvg ? `<span style="display:inline-flex;align-items:center;margin-right:6px;">${iconSvg}</span>` : '';
                            previewTitle.style.display = 'flex';
                            previewTitle.style.alignItems = 'center';
                            previewTitle.style.color = selectedColor;
                            previewTitle.innerHTML = `${iconSpan}${titleHtmlPreview}`;
                        } else {
                            previewTitle.style.display = 'none';
                            previewTitle.innerHTML = '';
                        }
                    }

                    if (previewBody) {
                        previewBody.innerHTML = bodyEditor?.innerHTML || '';
                        previewBody.style.color = '#374151';
                    }
                };
                updatePreview();

                // Title editor live update
                titleEditorEl?.addEventListener('input', updatePreview);

                // Title toolbar
                const titleToolbar = modal.querySelector('#callout-title-toolbar') as HTMLElement;
                titleToolbar?.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                    const btn = (e.target as HTMLElement).closest('[data-title-cmd]') as HTMLElement;
                    if (!btn) return;
                    const cmd = btn.dataset.titleCmd!;
                    titleEditorEl.focus();
                    document.execCommand(cmd, false);
                    btn.style.background = document.queryCommandState(cmd) ? `${selectedColor}22` : 'transparent';
                    btn.style.borderColor = document.queryCommandState(cmd) ? selectedColor : 'transparent';
                });

                // Body editor live update
                bodyEditor?.addEventListener('input', updatePreview);

                // Toolbar buttons
                const bodyToolbar = modal.querySelector('#callout-body-toolbar') as HTMLElement;
                bodyToolbar.addEventListener('mousedown', (e) => {
                    e.preventDefault(); // don't steal focus from bodyEditor
                    const btn = (e.target as HTMLElement).closest('[data-cmd]') as HTMLElement;
                    if (!btn) return;
                    const cmd = btn.dataset.cmd!;
                    bodyEditor.focus();
                    document.execCommand(cmd, false);
                    // Visual active state
                    btn.style.background = document.queryCommandState(cmd) ? `${color}22` : 'transparent';
                    btn.style.borderColor = document.queryCommandState(cmd) ? color : 'transparent';
                });

                // Update active states on selection change
                bodyEditor.addEventListener('keyup', () => updateBtnStates());
                bodyEditor.addEventListener('mouseup', () => updateBtnStates());
                const updateBtnStates = () => {
                    bodyToolbar.querySelectorAll('[data-cmd]').forEach((b: Element) => {
                        const btn = b as HTMLElement;
                        const active = document.queryCommandState(btn.dataset.cmd!);
                        btn.style.background = active ? `${color}22` : 'transparent';
                        btn.style.borderColor = active ? color : 'transparent';
                    });
                };

                // Color preset selection
                const colorPresetsEl = modal.querySelector('#callout-color-presets') as HTMLElement;
                colorPresetsEl.addEventListener('click', (e) => {
                    const btn = (e.target as HTMLElement).closest('button') as HTMLButtonElement;
                    if (!btn) return;
                    selectedColor = btn.dataset.presetBorder || selectedColor;
                    selectedBgColor = btn.dataset.presetBg || selectedBgColor;
                    // Update selected visual
                    colorPresetsEl.querySelectorAll('button').forEach(b => {
                        const isSelected = (b as HTMLElement).dataset.presetBorder === selectedColor;
                        (b as HTMLElement).style.outline = isSelected ? '2px solid #111' : 'none';
                        (b as HTMLElement).style.transform = isSelected ? 'scale(1.18)' : 'scale(1)';
                    });
                    // Update border buttons to use new color
                    borderBtnsEl.querySelectorAll('button').forEach(b => {
                        const isSelected = (b as HTMLElement).dataset.borderStyle === selectedBorderStyle;
                        (b as HTMLElement).style.border = `2px solid ${isSelected ? selectedColor : '#e5e7eb'}`;
                        (b as HTMLElement).style.background = isSelected ? `${selectedColor}18` : 'white';
                        (b as HTMLElement).style.color = isSelected ? selectedColor : '#374151';
                    });
                    // Update save button color
                    const saveBtn = modal.querySelector('#callout-modal-save') as HTMLElement;
                    if (saveBtn) saveBtn.style.background = selectedColor;
                    updatePreview();
                });

                // Border style selection
                const borderBtnsEl = modal.querySelector('#callout-border-btns') as HTMLElement;
                borderBtnsEl.addEventListener('click', (e) => {
                    const btn = (e.target as HTMLElement).closest('button') as HTMLButtonElement;
                    if (!btn) return;
                    selectedBorderStyle = (btn.dataset.borderStyle as 'left' | 'full' | 'top') || selectedBorderStyle;
                    borderBtnsEl.querySelectorAll('button').forEach(b => {
                        const isSelected = (b as HTMLElement).dataset.borderStyle === selectedBorderStyle;
                        (b as HTMLElement).style.border = `2px solid ${isSelected ? selectedColor : '#e5e7eb'}`;
                        (b as HTMLElement).style.background = isSelected ? `${selectedColor}18` : 'white';
                        (b as HTMLElement).style.color = isSelected ? selectedColor : '#374151';
                    });
                    updatePreview();
                });

                // Icon selection
                const iconGrid = modal.querySelector('#callout-icon-grid') as HTMLElement;
                iconGrid.addEventListener('click', (e) => {
                    const btn = (e.target as HTMLElement).closest('button') as HTMLButtonElement;
                    if (!btn) return;
                    selectedIcon = btn.dataset.iconVal || '';
                    iconGrid.querySelectorAll('button').forEach(b => {
                        const isSelected = b.dataset.iconVal === selectedIcon;
                        (b as HTMLElement).style.border = `2px solid ${isSelected ? selectedColor : '#e5e7eb'}`;
                        (b as HTMLElement).style.background = isSelected ? `${selectedColor}18` : 'white';
                        (b as HTMLElement).style.color = isSelected ? selectedColor : '#6b7280';
                    });
                    updatePreview();
                });

                // Gabarito toggle
                const gabaritoToggleBtn = modal.querySelector('#callout-gabarito-toggle') as HTMLButtonElement;
                const gabaritoHint = modal.querySelector('#callout-gabarito-hint') as HTMLElement;
                const updateGabaritoToggle = () => {
                    gabaritoToggleBtn.style.border = `2px solid ${isGabaritoEdit ? selectedColor : '#e5e7eb'}`;
                    gabaritoToggleBtn.style.background = isGabaritoEdit ? `${selectedColor}18` : 'white';
                    gabaritoToggleBtn.style.color = isGabaritoEdit ? selectedColor : '#6b7280';
                    gabaritoToggleBtn.innerHTML = `&#128065; ${isGabaritoEdit ? 'Gabarito ativado' : 'Transformar em gabarito'}`;
                    gabaritoHint.style.display = isGabaritoEdit ? 'block' : 'none';
                };
                gabaritoToggleBtn?.addEventListener('click', () => {
                    isGabaritoEdit = !isGabaritoEdit;
                    updateGabaritoToggle();
                });

                const closeModal = () => {
                    overlay.remove();
                    this.editModal = null;
                };

                modal.querySelector('#callout-modal-close')?.addEventListener('click', closeModal);
                modal.querySelector('#callout-modal-cancel')?.addEventListener('click', closeModal);
                overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });

                modal.querySelector('#callout-modal-remove')?.addEventListener('click', () => {
                    const parent = el.parentNode;
                    if (parent) {
                        const next = el.nextSibling;
                        if (next && next.nodeName === 'P' && (next as HTMLElement).innerHTML === '<br>') {
                            parent.removeChild(next);
                        }
                        parent.removeChild(el);
                    }
                    editor.dispatchEvent(new Event('input', { bubbles: true }));
                    closeModal();
                });

                modal.querySelector('#callout-modal-save')?.addEventListener('click', () => {
                    const titleEditorSave = modal.querySelector('#callout-edit-title') as HTMLElement;
                    const newTitleHtml = titleEditorSave?.innerHTML?.trim() || '';
                    const newTitleIsEmpty = !(titleEditorSave?.textContent?.trim());
                    const newBodyHtml = bodyEditor.innerHTML;

                    // Update data attributes
                    el.dataset.calloutIcon = selectedIcon;
                    el.dataset.calloutColor = selectedColor;
                    el.dataset.calloutBg = selectedBgColor;

                    // Update border style
                    el.style.removeProperty('border-left');
                    el.style.removeProperty('border');
                    el.style.removeProperty('border-top');
                    if (selectedBorderStyle === 'left') {
                        el.style.borderLeft = `4px solid ${selectedColor}`;
                    } else if (selectedBorderStyle === 'full') {
                        el.style.border = `2px solid ${selectedColor}`;
                    } else {
                        el.style.borderTop = `4px solid ${selectedColor}`;
                    }
                    el.style.background = selectedBgColor;

                    // Update edit button color
                    editBtn.style.background = selectedColor;

                    // Update title
                    if (titleEl) {
                        if (!newTitleIsEmpty || selectedIcon) {
                            const iconSvg = selectedIcon ? getCalloutIconSvg(selectedIcon, selectedColor) : '';
                            const iconSpan = iconSvg
                                ? `<span style="display:inline-flex;align-items:center;margin-right:8px;flex-shrink:0;" data-callout-icon-name="${selectedIcon}">${iconSvg}</span>`
                                : '';
                            titleEl.innerHTML = `${iconSpan}${newTitleHtml}`;
                            titleEl.style.color = selectedColor;
                            titleEl.style.display = '';
                        } else {
                            titleEl.innerHTML = '';
                            titleEl.style.display = 'none';
                        }
                    } else if (!newTitleIsEmpty || selectedIcon) {
                        const iconSvg = selectedIcon ? getCalloutIconSvg(selectedIcon, selectedColor) : '';
                        const iconSpan = iconSvg
                            ? `<span style="display:inline-flex;align-items:center;margin-right:8px;flex-shrink:0;" data-callout-icon-name="${selectedIcon}">${iconSvg}</span>`
                            : '';
                        const newTitleEl = document.createElement('p');
                        newTitleEl.className = 'callout-title';
                        newTitleEl.style.cssText = `margin: 0 0 8px 0; font-weight: 700; font-size: 15px; color: ${selectedColor};`;
                        newTitleEl.innerHTML = `${iconSpan}${newTitleHtml}`;
                        el.insertBefore(newTitleEl, bodyEl);
                    }

                    // Update body using innerHTML to preserve formatting and line breaks
                    if (bodyEl) {
                        bodyEl.innerHTML = newBodyHtml || '<p>Digite o conteúdo aqui...</p>';
                    }

                    // Update gabarito state
                    el.dataset.gabarito = String(isGabaritoEdit);
                    gabaritoRevealed = false;
                    applyGabaritoState();
                    revealBtn.style.background = selectedColor;

                    editor.dispatchEvent(new Event('input', { bubbles: true }));
                    closeModal();
                });

                setTimeout(() => {
                    (modal.querySelector('#callout-edit-title') as HTMLElement)?.focus();
                }, 50);
            };

            editBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                openEditModal();
            });

            el.addEventListener('dblclick', (e) => {
                e.stopPropagation();
                openEditModal();
            });
        });

        console.log(`✅ ${callouts.length} callouts inicializados`);
    }

    // ========== CLEANUP ==========
    cleanup() {
        const editor = this.editorRef.current;
        if (!editor) return;

        const badges = editor.querySelectorAll('.flashcard-badge');
        badges.forEach(badge => {
            const handler = (badge as any)._dragHandler;
            if (handler) {
                badge.removeEventListener('mousedown', handler);
                delete (badge as any)._dragHandler;
            }
        });

        document.removeEventListener('mousemove', this.handleBadgeDragMove);
        document.removeEventListener('mouseup', this.handleBadgeDragEnd);

        console.log('🧹 InlineContentManager cleanup concluído');
    }
}